import React, { useState, useEffect, useCallback } from 'react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import {
  Plus,
  Eye,
  EyeOff,
  Trash2,
  BarChart3,
  X,
  FileText,
} from 'lucide-react';
import tickerService, {
  Ticker,
  TickerFilters,
  CreateTickerData,
} from '../services/tickerService';
import schwabService, { SchwabQuote } from '../services/schwabService';
import comprehensiveAnalysisService from '../services/comprehensiveAnalysisService';
import TickerAnalysisSummaryModal from '../components/TickerAnalysisSummaryModal';

const Tickers: React.FC = () => {
  const [tickers, setTickers] = useState<Ticker[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addTickerLoading, setAddTickerLoading] = useState(false);
  const [filters, setFilters] = useState<TickerFilters>({
    page: 1,
    limit: 50,
    sortBy: 'symbol',
    sortOrder: 'asc',
  });
  const [totalTickers, setTotalTickers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Add ticker modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTickerSymbol, setNewTickerSymbol] = useState('');
  const [schwabQuote, setSchwabQuote] = useState<SchwabQuote | null>(null);
  const [searchResults, setSearchResults] = useState<Ticker[]>([]);

  // Comprehensive analysis modal state
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState<Ticker | null>(null);
  const [analysis, setAnalysis] = useState<any>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Analysis summary modal state
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryTicker, setSummaryTicker] = useState<Ticker | null>(null);

  // Progressive analysis state
  const [horizonResults, setHorizonResults] = useState<{
    [horizon: string]: {
      status: 'pending' | 'loading' | 'completed' | 'error';
      data?: any;
      error?: string;
    };
  }>({});
  const [overallProgress, setOverallProgress] = useState(0);

  // Asset type options
  const assetTypes = [
    'equity',
    'option',
    'future',
    'commodity',
    'forex',
    'crypto',
    'bond',
    'etf',
    'mutual_fund',
    'index',
    'warrant',
    'adr',
    'preferred',
    'real_estate',
    'currency',
  ];

  const exchanges = [
    'NYSE',
    'NASDAQ',
    'AMEX',
    'ARCA',
    'CBOE',
    'CME',
    'ICE',
    'OANDA',
    'BINANCE',
    'COINBASE',
    'LSE',
    'TSE',
    'HKEX',
    'TSX',
    'ASX',
    'NSE',
    'BSE',
    'OTC',
    'PINK',
    'OTHER',
  ];

  // Fetch tickers
  const fetchTickers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await tickerService.getTickers(filters);
      setTickers(response.tickers || []);
      setTotalTickers(response.total || 0);
      setTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error('Error fetching tickers:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Search tickers
  const searchTickers = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await tickerService.searchTickers(query, 10);
      setSearchResults(results || []);
    } catch (error) {
      console.error('Error searching tickers:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Get Schwab quote for symbol
  const getSchwabQuote = async (symbol: string) => {
    try {
      const quote = await schwabService.getQuote(symbol);
      setSchwabQuote(quote);
      return quote;
    } catch (error) {
      console.error('Error getting Schwab quote:', error);
      setSchwabQuote(null);
      return null;
    }
  };

  // Add new ticker
  const addTicker = async () => {
    if (!newTickerSymbol.trim()) {
      return;
    }

    try {
      setAddTickerLoading(true);

      // Create minimal ticker data - backend will enrich it
      const tickerData: CreateTickerData = {
        symbol: newTickerSymbol.trim().toUpperCase(),
        // Optionally include Schwab quote fields that are allowed
        ...(schwabQuote && {
          name: schwabQuote.description,
          exchange: schwabQuote.exchange,
          description: schwabQuote.description,
        }),
        assetType: 'equity',
        isActive: true,
        isTradable: true,
        includeInAnalysis: true,
        analysisPriority: 5,
        isWatchlist: false,
        tags: ['dashboard-import'],
        keywords: [newTickerSymbol.trim().toLowerCase()],
      };

      await tickerService.createTicker(tickerData);

      // Reset form and refresh tickers
      setNewTickerSymbol('');
      setSchwabQuote(null);
      setShowAddModal(false);
      fetchTickers();
    } catch (error) {
      console.error('Error adding ticker:', error);
      alert(
        `Failed to add ticker: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setAddTickerLoading(false);
    }
  };

  // Toggle ticker status
  const toggleTickerStatus = async (
    tickerId: string,
    field: 'isActive' | 'isTradable' | 'includeInAnalysis' | 'isWatchlist',
  ) => {
    try {
      const ticker = tickers.find((t) => t._id === tickerId);
      if (!ticker) return;

      const updateData = { [field]: !ticker[field] };
      await tickerService.updateTicker(tickerId, updateData);

      // Update local state
      setTickers((prev) =>
        prev.map((t) =>
          t._id === tickerId ? { ...t, [field]: !t[field] } : t,
        ),
      );
    } catch (error) {
      console.error('Error updating ticker:', error);
      alert('Failed to update ticker status');
    }
  };

  // Delete ticker
  const deleteTicker = async (tickerId: string) => {
    // Use window.confirm instead of confirm for better ESLint compatibility
    if (!window.confirm('Are you sure you want to delete this ticker?')) {
      return;
    }

    try {
      await tickerService.deleteTicker(tickerId);

      // Remove from local state
      setTickers((prev) => prev.filter((t) => t._id !== tickerId));
    } catch (error) {
      console.error('Error deleting ticker:', error);
      alert('Failed to delete ticker');
    }
  };

  // Handle symbol input change
  const handleSymbolChange = async (symbol: string) => {
    setNewTickerSymbol(symbol);

    if (symbol.trim().length >= 2) {
      await searchTickers(symbol);
    } else {
      setSearchResults([]);
    }
  };

  // Handle symbol selection from search
  const handleSymbolSelect = async (symbol: string) => {
    setNewTickerSymbol(symbol);
    setSearchResults([]);
    await getSchwabQuote(symbol);
  };

  // Handle manual quote fetch
  const handleFetchQuote = async () => {
    if (!newTickerSymbol.trim()) {
      alert('Please enter a symbol first');
      return;
    }

    try {
      await getSchwabQuote(newTickerSymbol.trim());
    } catch (error) {
      alert(
        `Failed to fetch quote for ${newTickerSymbol}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof TickerFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Comprehensive analysis functions
  const openAnalysisModal = (ticker: Ticker) => {
    setSelectedTicker(ticker);
    setShowAnalysisModal(true);
    setAnalysis(null);
    setAnalysisError(null);
  };

  const closeAnalysisModal = () => {
    setShowAnalysisModal(false);
    setSelectedTicker(null);
    setAnalysis(null);
    setAnalysisError(null);
    setHorizonResults({});
    setOverallProgress(0);
  };

  // Analysis summary functions
  const openSummaryModal = (ticker: Ticker) => {
    setSummaryTicker(ticker);
    setShowSummaryModal(true);
  };

  const closeSummaryModal = () => {
    setShowSummaryModal(false);
    setSummaryTicker(null);
  };

  const loadComprehensiveAnalysis = useCallback(async () => {
    if (!selectedTicker) return;

    setAnalysisLoading(true);
    setAnalysisError(null);
    setOverallProgress(0);

    // Initialize horizon results
    const horizons = ['1hr', '1d', '1w', '1m', '1yr'];
    const initialResults: {
      [horizon: string]: {
        status: 'pending' | 'loading' | 'completed' | 'error';
        data?: any;
        error?: string;
      };
    } = {};
    horizons.forEach((horizon) => {
      initialResults[horizon] = { status: 'pending' };
    });
    setHorizonResults(initialResults);

    try {
      // Use the new multi-horizon analysis with progressive updates
      const result = await comprehensiveAnalysisService.analyzeAllHorizons(
        selectedTicker.symbol,
        false,
        (
          horizon: string,
          status: 'loading' | 'completed' | 'error',
          data?: any,
          error?: string,
        ) => {
          setHorizonResults((prev) => {
            const newResults = {
              ...prev,
              [horizon]: { status, data, error },
            };

            // Update overall progress
            const completedCount = Object.values(newResults).filter(
              (r) => r.status === 'completed' || r.status === 'error',
            ).length;
            setOverallProgress((completedCount / horizons.length) * 100);

            return newResults;
          });
        },
      );

      // Format the analysis for display
      const formattedResult =
        comprehensiveAnalysisService.formatMultiHorizonAnalysis(result);
      setAnalysis(formattedResult);
    } catch (err: any) {
      setAnalysisError(err.message);
    } finally {
      setAnalysisLoading(false);
    }
  }, [selectedTicker]);

  // Format price change
  const formatPriceChange = (change: number, changePercent: number) => {
    const isPositive = change >= 0;
    const sign = isPositive ? '+' : '';
    return (
      <span className={isPositive ? 'text-green-600' : 'text-red-600'}>
        {sign}
        {change.toFixed(2)} ({sign}
        {changePercent.toFixed(2)}%)
      </span>
    );
  };

  // Load tickers on mount and filter changes
  useEffect(() => {
    fetchTickers();
  }, [fetchTickers]);

  // Load analysis when modal opens
  useEffect(() => {
    if (showAnalysisModal && selectedTicker) {
      loadComprehensiveAnalysis();
    }
  }, [showAnalysisModal, selectedTicker, loadComprehensiveAnalysis]);

  function getDisplayRecommendation(
    action: string,
    horizons: Record<string, any>,
  ) {
    // Find the best horizon (highest confidence)
    let bestConfidence = -1;
    let bestTrend = null;
    Object.values(horizons || {}).forEach((h: any) => {
      if (h?.analysis?.confidence > bestConfidence) {
        bestConfidence = h.analysis.confidence;
        bestTrend = h.analysis.technical?.trend;
      }
    });
    // If bearish and recommendation is SELL/STRONG_SELL, show SELL
    if (
      bestTrend === 'bearish' &&
      (action === 'SELL' || action === 'STRONG_SELL')
    )
      return 'SELL';
    // If bullish and recommendation is BUY/STRONG_BUY, show BUY
    if (
      bestTrend === 'bullish' &&
      (action === 'BUY' || action === 'STRONG_BUY')
    )
      return 'BUY';
    // Otherwise, show HOLD or the original action
    return action;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TierPageBanner
          title="Ticker universe"
          description="Symbols analyzed here may surface in tier-scoped recommendations—primarily Tier 6 orchestration and Tier 1 ETF sleeves."
          thesisTimeframe="1_2_weeks"
        />
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Ticker Management
              </h1>
              <p className="mt-2 text-gray-600">
                Manage your ticker collection for analysis and trading
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Ticker
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                placeholder="Symbol, name, or description..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Asset Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset Type
              </label>
              <select
                value={filters.assetType || ''}
                onChange={(e) =>
                  handleFilterChange('assetType', e.target.value || undefined)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {assetTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() +
                      type.slice(1).replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            {/* Exchange */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exchange
              </label>
              <select
                value={filters.exchange || ''}
                onChange={(e) =>
                  handleFilterChange('exchange', e.target.value || undefined)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Exchanges</option>
                {exchanges.map((exchange) => (
                  <option key={exchange} value={exchange}>
                    {exchange}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.isActive?.toString() || ''}
                onChange={(e) =>
                  handleFilterChange(
                    'isActive',
                    e.target.value === 'true'
                      ? true
                      : e.target.value === 'false'
                        ? false
                        : undefined,
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tickers Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Symbol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Exchange
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tickers.map((ticker) => (
                      <tr key={ticker._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {ticker.symbol}
                            </span>
                            {ticker.isWatchlist && (
                              <Eye className="h-4 w-4 ml-2 text-blue-500" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {ticker.name}
                          </div>
                          {ticker.sector && (
                            <div className="text-sm text-gray-500">
                              {ticker.sector}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {ticker.assetType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {ticker.exchange}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {ticker.lastPrice ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                ${ticker.lastPrice.lastPrice.toFixed(2)}
                              </div>
                              <div className="text-sm">
                                {formatPriceChange(
                                  ticker.lastPrice.change,
                                  ticker.lastPrice.changePercent,
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              onClick={() =>
                                toggleTickerStatus(ticker._id, 'isActive')
                              }
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                ticker.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {ticker.isActive ? 'Active' : 'Inactive'}
                            </button>
                            <button
                              onClick={() =>
                                toggleTickerStatus(
                                  ticker._id,
                                  'includeInAnalysis',
                                )
                              }
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                                ticker.includeInAnalysis
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {ticker.includeInAnalysis ? 'Analysis' : 'Paused'}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openSummaryModal(ticker)}
                              className="p-1 rounded text-green-600 hover:text-green-800"
                              title="Analysis Summary"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openAnalysisModal(ticker)}
                              className="p-1 rounded text-blue-600 hover:text-blue-800"
                              title="Comprehensive Analysis"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                toggleTickerStatus(ticker._id, 'isWatchlist')
                              }
                              className={`p-1 rounded ${
                                ticker.isWatchlist
                                  ? 'text-blue-600 hover:text-blue-800'
                                  : 'text-gray-400 hover:text-gray-600'
                              }`}
                              title="Toggle Watchlist"
                            >
                              {ticker.isWatchlist ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => deleteTicker(ticker._id)}
                              className="p-1 rounded text-red-400 hover:text-red-600"
                              title="Delete Ticker"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(filters.page! - 1)}
                      disabled={filters.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(filters.page! + 1)}
                      disabled={filters.page === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">
                          {(filters.page! - 1) * filters.limit! + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(
                            filters.page! * filters.limit!,
                            totalTickers,
                          )}
                        </span>{' '}
                        of <span className="font-medium">{totalTickers}</span>{' '}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange(filters.page! - 1)}
                          disabled={filters.page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            const page = i + 1;
                            return (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  filters.page === page
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          },
                        )}
                        <button
                          onClick={() => handlePageChange(filters.page! + 1)}
                          disabled={filters.page === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add Ticker Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Add New Ticker
                </h3>

                {/* Symbol Input */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Symbol
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Enter symbol (e.g., AAPL)"
                      value={newTickerSymbol}
                      onChange={(e) => handleSymbolChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {searchLoading && (
                      <div className="absolute right-3 top-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                      {searchResults.map((result) => (
                        <button
                          key={result._id}
                          onClick={() => handleSymbolSelect(result.symbol)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium">{result.symbol}</div>
                          <div className="text-sm text-gray-500">
                            {result.name}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Manual Quote Fetch */}
                  {newTickerSymbol.trim() &&
                    !schwabQuote &&
                    searchResults.length === 0 && (
                      <div className="mt-2">
                        <button
                          onClick={handleFetchQuote}
                          className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          Fetch Quote for "{newTickerSymbol.trim()}"
                        </button>
                      </div>
                    )}
                </div>

                {/* Schwab Quote Preview */}
                {schwabQuote && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Quote Preview
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div>
                        <span className="font-medium">Name:</span>{' '}
                        {schwabQuote.description}
                      </div>
                      <div>
                        <span className="font-medium">Exchange:</span>{' '}
                        {schwabQuote.exchangeName}
                      </div>
                      <div>
                        <span className="font-medium">Price:</span> $
                        {schwabQuote.lastPrice.toFixed(2)}
                      </div>
                      <div>
                        <span className="font-medium">Change:</span>{' '}
                        {formatPriceChange(
                          schwabQuote.netChange,
                          schwabQuote.netPercentChangeInDouble,
                        )}
                      </div>
                      {schwabQuote.peRatio && (
                        <div>
                          <span className="font-medium">P/E:</span>{' '}
                          {schwabQuote.peRatio.toFixed(2)}
                        </div>
                      )}
                      {schwabQuote.divYield > 0 && (
                        <div>
                          <span className="font-medium">Yield:</span>{' '}
                          {schwabQuote.divYield.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setNewTickerSymbol('');
                      setSchwabQuote(null);
                      setSearchResults([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addTicker}
                    disabled={
                      !newTickerSymbol.trim() ||
                      !schwabQuote ||
                      addTickerLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {addTickerLoading ? 'Adding...' : 'Add Ticker'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comprehensive Analysis Modal */}
        {showAnalysisModal && selectedTicker && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-medium text-gray-900">
                  Comprehensive Analysis: {selectedTicker.symbol}
                </h3>
                <button
                  onClick={closeAnalysisModal}
                  className="p-1 rounded text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Horizon Selection */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3">Investment Horizons</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Analysis will be performed across all investment horizons for
                  comprehensive insights.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {comprehensiveAnalysisService
                    .getAllHorizonConfigs()
                    .map((config) => (
                      <div
                        key={config.id}
                        className="p-3 bg-white rounded border"
                      >
                        <div className="font-medium text-sm">{config.name}</div>
                        <div className="text-xs text-gray-500">
                          {config.description}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Risk: {config.riskProfile}
                        </div>
                      </div>
                    ))}
                </div>
                <button
                  onClick={loadComprehensiveAnalysis}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Start Progressive Analysis
                </button>
              </div>

              {/* Analysis Content */}
              {analysisLoading ? (
                <div className="space-y-6">
                  {/* Overall Progress */}
                  <div className="bg-white p-6 rounded-lg border">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold">
                        Analysis Progress
                      </h4>
                      <span className="text-sm font-medium text-blue-600">
                        {Math.round(overallProgress)}% Complete
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${overallProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Analyzing {selectedTicker.symbol} across 5 investment
                      horizons...
                    </p>
                  </div>

                  {/* Progressive Horizon Results */}
                  <div className="space-y-4">
                    {Object.entries(horizonResults).map(([horizon, result]) => {
                      const config =
                        comprehensiveAnalysisService.getHorizonConfig(horizon);
                      return (
                        <div
                          key={horizon}
                          className="bg-white p-6 rounded-lg border"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h4 className="text-lg font-semibold">
                                {config?.name || horizon} Analysis
                              </h4>
                              <p className="text-sm text-gray-500">
                                {config?.description}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {result.status === 'pending' && (
                                <div className="flex items-center space-x-2">
                                  <div className="w-4 h-4 bg-gray-200 rounded-full"></div>
                                  <span className="text-sm text-gray-500">
                                    Pending
                                  </span>
                                </div>
                              )}
                              {result.status === 'loading' && (
                                <div className="flex items-center space-x-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  <span className="text-sm text-blue-600">
                                    Analyzing...
                                  </span>
                                </div>
                              )}
                              {result.status === 'completed' && result.data && (
                                <div className="flex items-center space-x-2">
                                  <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  </div>
                                  <span
                                    className="px-2 py-1 rounded-full text-xs font-medium"
                                    style={{
                                      backgroundColor:
                                        comprehensiveAnalysisService.getRecommendationColor(
                                          result.data.analysis
                                            ?.recommendation || 'HOLD',
                                        ) + '20',
                                      color:
                                        comprehensiveAnalysisService.getRecommendationColor(
                                          result.data.analysis
                                            ?.recommendation || 'HOLD',
                                        ),
                                    }}
                                  >
                                    {result.data.analysis?.recommendation ||
                                      'HOLD'}
                                  </span>
                                </div>
                              )}
                              {result.status === 'error' && (
                                <div className="flex items-center space-x-2">
                                  <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  </div>
                                  <span className="text-sm text-red-600">
                                    Failed
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Show results for completed analyses */}
                          {result.status === 'completed' &&
                            result.data &&
                            result.data.analysis && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <h5 className="font-semibold mb-2">
                                    Quick Summary
                                  </h5>
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span>Confidence:</span>
                                      <span className="font-medium">
                                        {result.data.analysis.confidence}%
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Trend:</span>
                                      <span
                                        className="font-medium"
                                        style={{
                                          color:
                                            comprehensiveAnalysisService.getTrendColor(
                                              result.data.analysis.technical
                                                ?.trend || 'neutral',
                                            ),
                                        }}
                                      >
                                        {result.data.analysis.technical
                                          ?.trend || 'neutral'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>Risk Level:</span>
                                      <span className="font-medium">
                                        {result.data.analysis.riskLevel}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h5 className="font-semibold mb-2">
                                    AI Insights
                                  </h5>
                                  <p className="text-sm text-gray-600 mb-2">
                                    {result.data.analysis.aiInsights?.reasoning?.substring(
                                      0,
                                      100,
                                    )}
                                    ...
                                  </p>
                                  <div className="text-sm">
                                    <span className="font-medium">
                                      Confidence:{' '}
                                    </span>
                                    {
                                      result.data.analysis.aiInsights
                                        ?.confidenceScore
                                    }
                                    %
                                  </div>
                                </div>
                              </div>
                            )}

                          {/* Show error for failed analyses */}
                          {result.status === 'error' && (
                            <div className="text-sm text-red-600">
                              {result.error || 'Analysis failed'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : analysisError ? (
                <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-lg font-semibold text-red-800 mb-2">
                    Error
                  </h4>
                  <p className="text-red-600">{analysisError}</p>
                  <button
                    onClick={loadComprehensiveAnalysis}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Retry
                  </button>
                </div>
              ) : analysis ? (
                <div className="space-y-6">
                  {/* Header with Overall Recommendation */}
                  <div className="bg-white p-6 rounded-lg border">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold">
                        Overall Recommendation
                      </h4>
                      <span
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor:
                            comprehensiveAnalysisService.getRecommendationColor(
                              getDisplayRecommendation(
                                analysis.overallRecommendation.action,
                                analysis.horizons,
                              ),
                            ) + '20',
                          color:
                            comprehensiveAnalysisService.getRecommendationColor(
                              getDisplayRecommendation(
                                analysis.overallRecommendation.action,
                                analysis.horizons,
                              ),
                            ),
                        }}
                      >
                        {getDisplayRecommendation(
                          analysis.overallRecommendation.action,
                          analysis.horizons,
                        )}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-3">
                      {analysis.overallRecommendation.reasoning}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Confidence:</span>{' '}
                        {comprehensiveAnalysisService.formatPercentage(
                          analysis.overallRecommendation.confidence,
                        )}
                      </div>
                      <div>
                        <span className="font-medium">Time Horizon:</span>{' '}
                        {comprehensiveAnalysisService.getTimeHorizonDescription(
                          analysis.overallRecommendation.timeHorizon,
                        )}
                      </div>
                      <div>
                        <span className="font-medium">Risk Level:</span>{' '}
                        {analysis.overallRecommendation.riskLevel}
                      </div>
                      <div>
                        <span className="font-medium">Score:</span>{' '}
                        {comprehensiveAnalysisService.calculateOverallScore(
                          analysis,
                        )}
                        /100
                      </div>
                    </div>
                  </div>

                  {/* Market Data */}
                  <div className="bg-white p-6 rounded-lg border">
                    <h4 className="text-lg font-semibold mb-4">Market Data</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Current Price</p>
                        <p className="text-lg font-semibold">
                          {analysis.marketData?.currentPrice &&
                          analysis.marketData.currentPrice > 0
                            ? `$${comprehensiveAnalysisService.formatPrice(analysis.marketData.currentPrice)}`
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Change</p>
                        <p
                          className={`text-lg font-semibold ${
                            (analysis.marketData?.priceChange || 0) > 0
                              ? 'text-green-600'
                              : (analysis.marketData?.priceChange || 0) < 0
                                ? 'text-red-600'
                                : 'text-gray-500'
                          }`}
                        >
                          {typeof analysis.marketData?.priceChange ===
                            'number' && analysis.marketData.priceChange !== 0
                            ? `${analysis.marketData.priceChange > 0 ? '+' : ''}${comprehensiveAnalysisService.formatPrice(analysis.marketData.priceChange)} (${comprehensiveAnalysisService.formatPercentage(analysis.marketData.priceChangePercent)})`
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Volume</p>
                        <p className="text-lg font-semibold">
                          {analysis.marketData?.volume &&
                          analysis.marketData.volume > 0
                            ? comprehensiveAnalysisService.formatLargeNumber(
                                analysis.marketData.volume,
                              )
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Data Quality</p>
                        <p className="text-lg font-semibold">
                          {analysis.metadata?.dataQuality &&
                          analysis.metadata.dataQuality > 0
                            ? `${analysis.metadata.dataQuality}%`
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Horizon Analysis */}
                  <div className="space-y-4">
                    {Object.entries(analysis.horizons).map(
                      ([horizon, horizonAnalysis]: [string, any]) => {
                        if (
                          !horizonAnalysis.success ||
                          !horizonAnalysis.analysis
                        ) {
                          return (
                            <div
                              key={horizon}
                              className="bg-white p-6 rounded-lg border"
                            >
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-semibold">
                                  {comprehensiveAnalysisService.getHorizonConfig(
                                    horizon,
                                  )?.name || horizon}{' '}
                                  Analysis
                                </h4>
                                <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                  Failed
                                </span>
                              </div>
                              <p className="text-red-600">
                                {horizonAnalysis.error || 'Analysis failed'}
                              </p>
                            </div>
                          );
                        }

                        const data = horizonAnalysis.analysis;
                        return (
                          <div
                            key={horizon}
                            className="bg-white p-6 rounded-lg border"
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-semibold">
                                {comprehensiveAnalysisService.getHorizonConfig(
                                  horizon,
                                )?.name || horizon}{' '}
                                Analysis
                              </h4>
                              <span
                                className="px-3 py-1 rounded-full text-sm font-medium"
                                style={{
                                  backgroundColor:
                                    horizonAnalysis.recommendationColor + '20',
                                  color: horizonAnalysis.recommendationColor,
                                }}
                              >
                                {data.recommendation}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Technical Summary */}
                              <div>
                                <h5 className="font-semibold mb-2">
                                  Technical Analysis
                                </h5>
                                <p className="text-sm text-gray-600 mb-3">
                                  {data.reasoning}
                                </p>
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span>Confidence:</span>
                                    <span>{data.confidence}%</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span>Trend:</span>
                                    <span
                                      style={{
                                        color:
                                          comprehensiveAnalysisService.getTrendColor(
                                            data.technical.trend,
                                          ),
                                      }}
                                    >
                                      {data.technical.trend}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span>Strength:</span>
                                    <span>{data.technical.strength}%</span>
                                  </div>
                                </div>
                              </div>

                              {/* AI Insights */}
                              <div>
                                <h5 className="font-semibold mb-2">
                                  AI Insights
                                </h5>
                                <p className="text-sm text-gray-600 mb-3">
                                  {data.aiInsights.reasoning}
                                </p>
                                <div className="space-y-2">
                                  <div className="flex justify-between text-sm">
                                    <span>Confidence:</span>
                                    <span>
                                      {data.aiInsights.confidenceScore}%
                                    </span>
                                  </div>
                                  <div className="text-sm">
                                    <strong>Key Factors:</strong>
                                    <ul className="list-disc list-inside mt-1">
                                      {data.aiInsights.keyFactors
                                        ?.slice(0, 3)
                                        .map(
                                          (factor: string, index: number) => (
                                            <li key={index} className="text-xs">
                                              {factor}
                                            </li>
                                          ),
                                        )}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Price Targets */}
                            {data.priceTargets && (
                              <div className="mt-4">
                                <h5 className="font-semibold mb-2">
                                  Price Targets
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  {Object.entries(
                                    data.priceTargets.priceTargets ||
                                      data.priceTargets,
                                  ).map(([type, target]: [string, any]) => (
                                    <div
                                      key={type}
                                      className="bg-gray-50 p-3 rounded"
                                    >
                                      <div className="text-sm font-medium capitalize">
                                        {type}
                                      </div>
                                      <div className="flex justify-between text-sm">
                                        <span>Target:</span>
                                        <span className="font-semibold">
                                          $
                                          {comprehensiveAnalysisService.formatPrice(
                                            target.price,
                                          )}
                                        </span>
                                      </div>
                                      <div className="flex justify-between text-sm">
                                        <span>Confidence:</span>
                                        <span>{target.confidence}%</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>

                  {/* Analysis Summary */}
                  <div className="bg-white p-6 rounded-lg border">
                    <h4 className="text-lg font-semibold mb-4">
                      Horizon Summary
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {comprehensiveAnalysisService.getHorizonSummary(
                            analysis,
                          )?.buySignals || 0}
                        </p>
                        <p className="text-sm text-gray-500">Buy Signals</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600">
                          {comprehensiveAnalysisService.getHorizonSummary(
                            analysis,
                          )?.sellSignals || 0}
                        </p>
                        <p className="text-sm text-gray-500">Sell Signals</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-yellow-600">
                          {comprehensiveAnalysisService.getHorizonSummary(
                            analysis,
                          )?.holdSignals || 0}
                        </p>
                        <p className="text-sm text-gray-500">Hold Signals</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {comprehensiveAnalysisService.getHorizonSummary(
                            analysis,
                          )?.totalHorizons || 0}
                        </p>
                        <p className="text-sm text-gray-500">Total Horizons</p>
                      </div>
                    </div>
                    <div className="mt-4 text-center">
                      <p className="text-sm text-gray-600">
                        Consensus:{' '}
                        <span className="font-semibold">
                          {comprehensiveAnalysisService.getHorizonSummary(
                            analysis,
                          )?.consensus || 'NEUTRAL'}
                        </span>
                      </p>
                      <p className="text-sm text-gray-600">
                        Best Horizon:{' '}
                        <span className="font-semibold">
                          {comprehensiveAnalysisService.getBestHorizon(
                            analysis,
                          ) || 'None'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No analysis available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analysis Summary Modal */}
        <TickerAnalysisSummaryModal
          ticker={summaryTicker}
          isOpen={showSummaryModal}
          onClose={closeSummaryModal}
        />
      </div>
    </div>
  );
};

export default Tickers;
